import React, { useState, useRef } from "react";
import {
  Modal,
  Button,
  Typography,
  Space,
  Divider,
  Tag,
  Table,
  Alert,
  Spin,
} from "antd";
import {
  UploadOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileExcelOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { API_CONST } from "../../const";

const { Text, Title } = Typography;

const BulkUploadJobs = ({ visible, onClose, userRole, onUploadComplete }) => {
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const isAdmin = userRole?.toLowerCase() === "admin";
  const roleParam = isAdmin ? "admin" : "customer";

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${API_CONST.BULK_UPLOAD_TEMPLATE}?role=${roleParam}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        isAdmin ? "job_upload_template_admin.xlsx" : "job_upload_template.xlsx"
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setResult({
        isError: true,
        message: "Failed to download template. Please try again.",
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setResult(null);
    try {
      const token = localStorage.getItem("authToken");
      const formData = new FormData();
      formData.append("file", selectedFile);
      const response = await axios.post(API_CONST.BULK_UPLOAD_JOBS, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      setResult(response.data);
      if (response.data.succeeded > 0 && onUploadComplete) {
        onUploadComplete();
      }
    } catch (error) {
      setResult({
        isError: true,
        message:
          error.response?.data?.message || "Upload failed. Please try again.",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setResult(null);
    setUploading(false);
    setDownloading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    onClose();
  };

  const errorTableColumns = [
    { title: "Row", dataIndex: "row", key: "row", width: 65 },
    { title: "Role", dataIndex: "role", key: "role", width: 180, ellipsis: true },
    { title: "Reason", dataIndex: "reason", key: "reason" },
  ];

  return (
    <Modal
      title={
        <Space>
          <FileExcelOutlined style={{ color: "#00d9a9" }} />
          Bulk Upload Jobs
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={720}
    >
      <div style={{ padding: "8px 0" }}>
        <Title level={5} style={{ marginTop: 0 }}>
          Step 1 — Download Template
        </Title>
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          Download the Excel template for your role. The file includes a{" "}
          <strong>Job Upload Template</strong> sheet with an example row and an{" "}
          <strong>Instructions</strong> sheet explaining every column.
        </Text>
        <Button
          icon={downloading ? <LoadingOutlined /> : <DownloadOutlined />}
          onClick={handleDownloadTemplate}
          loading={downloading}
          style={{ marginBottom: 16 }}
        >
          Download {isAdmin ? "Admin" : "Customer"} Template
        </Button>

        <Divider />

        <Title level={5}>Step 2 — Upload Completed Template</Title>
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          Fill in your data in the template and upload it here. All required
          columns must be present. Valid rows are saved even if some rows fail.
        </Text>

        <div
          style={{
            border: "2px dashed #d9d9d9",
            borderRadius: 8,
            padding: "24px",
            textAlign: "center",
            background: "#fafafa",
            marginBottom: 16,
            cursor: "pointer",
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />
          <UploadOutlined
            style={{ fontSize: 32, color: "#bfbfbf", marginBottom: 8 }}
          />
          <div>
            {selectedFile ? (
              <Space>
                <FileExcelOutlined style={{ color: "#52c41a" }} />
                <Text>{selectedFile.name}</Text>
                <Text type="secondary">
                  ({(selectedFile.size / 1024).toFixed(1)} KB)
                </Text>
              </Space>
            ) : (
              <Text type="secondary">Click to select an Excel file (.xlsx)</Text>
            )}
          </div>
        </div>

        <Button
          type="primary"
          icon={<UploadOutlined />}
          onClick={handleUpload}
          loading={uploading}
          disabled={!selectedFile}
          style={{
            background: "#00d9a9",
            borderColor: "#00d9a9",
            width: "100%",
          }}
        >
          {uploading ? "Uploading..." : "Upload & Create Jobs"}
        </Button>

        {result && (
          <>
            <Divider />
            <Title level={5}>Upload Results</Title>

            {result.isError ? (
              <Alert type="error" message={result.message} showIcon />
            ) : (
              <>
                <Space style={{ marginBottom: 12 }}>
                  <Tag color="blue" style={{ fontSize: 13, padding: "3px 10px" }}>
                    {result.total} Total
                  </Tag>
                  <Tag
                    color="green"
                    icon={<CheckCircleOutlined />}
                    style={{ fontSize: 13, padding: "3px 10px" }}
                  >
                    {result.succeeded} Created
                  </Tag>
                  {result.failed?.length > 0 && (
                    <Tag
                      color="red"
                      icon={<CloseCircleOutlined />}
                      style={{ fontSize: 13, padding: "3px 10px" }}
                    >
                      {result.failed.length} Failed
                    </Tag>
                  )}
                </Space>

                {result.failed?.length > 0 && (
                  <>
                    <Text
                      type="secondary"
                      style={{ display: "block", marginBottom: 8 }}
                    >
                      The following rows had errors and were skipped:
                    </Text>
                    <Table
                      dataSource={result.failed}
                      columns={errorTableColumns}
                      rowKey="row"
                      size="small"
                      pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
                      scroll={{ y: 200 }}
                    />
                  </>
                )}

                {result.succeeded > 0 && (
                  <Alert
                    type="success"
                    message={`${result.succeeded} job requirement${
                      result.succeeded !== 1 ? "s" : ""
                    } created successfully.`}
                    style={{ marginTop: 12 }}
                    showIcon
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default BulkUploadJobs;
